import { UserSelector } from '@tencent/tds-ui';
import { Modal, Form, Input, message, Row, Col, Radio, Cascader, Divider } from 'antd';
import classNames from 'classnames';
import React, { useState, useEffect } from 'react';
import { getUserId, isInternal } from '@/infrastructure/auth';
import { BusiCode } from '@/infrastructure/axios/code';
import { BASE_NAME } from '@/infrastructure/history';
import { getAppIcon, AccessPlatformType } from '@/infrastructure/utils/app';
import { getTips } from '@/presentation/components/issue-list/components/tips';
import { IAddProductOaReq, PlatformType, productOAServer } from '@/service/productoa';
import {
  GetDepartmentsByUser,
} from './api';
import styles from './style.module.scss';

export interface AddAppReq {
  rdmProductId: string;
  bundleId: string;
  pid: number;
  name: string;
  type: number;
  logoUrl: string;
  ownerId: string;
  desc: string;
  appFrom: number;
  admins: string[];
  department: string;
  appForm: number;
}

interface FormFieldType {
  productName: string;
  bundleId: string;
  department: string;
  admins: string[];
  appType: number[];
  platform: number;
  appForm: number;
}

interface SelectAppType  {
  value?: (number | string)[];
  onChange?: (value: (number | string)[]) => void
}

export enum Id {
  MIG = 954,
  SNG = 955,
  OMG = 957,
  IEG = 956,
  CDG = 953,
  TEG = 958,
  CIO = 9832,
  EI = 1000009,
  WXG = 14129,
  S3 = 2234,
  S1 = 78,
  S2 = 2233,
  WeBank = 88888,
}

export const BG_ID = {
  [Id.MIG]: 1, // MIG
  [Id.SNG]: 4, // SNG
  [Id.OMG]: 6, // OMG
  [Id.IEG]: 7, // IEG
  [Id.CDG]: 9, // CDG
  [Id.TEG]: 11, // TEG
  [Id.CIO]: 13, // 公司级组织
  [Id.EI]: 12, // 企业互联
  [Id.WXG]: 14, // WXG
  [Id.S3]: 15, // S3
  [Id.S1]: 16, // S1
  [Id.S2]: 17, // S2
  [Id.WeBank]: 18, // WeBank
};

export const ALLTYPE_OPTIONS = [
  { label: '软件', value: 1 },
  { label: '游戏', value: 2 },
  { label: '办公商务', value: 3 },
  { label: '聊天通讯', value: 4 },
  { label: '系统输入', value: 5 },
  { label: '手机美化', value: 6 },
  { label: '阅读学习', value: 7 },
  { label: '出行地图', value: 8 },
  { label: '影音视听', value: 9 },
  { label: '儿童亲子', value: 10 },
  { label: '拍摄美化', value: 11 },
  { label: '购物时尚', value: 12 },
  { label: '生活娱乐', value: 13 },
  { label: '实用工具', value: 14 },
  { label: '动作冒险', value: 15 },
  { label: '飞行射击', value: 16 },
  { label: '角色扮演', value: 17 },
  { label: '经营策略', value: 18 },
  { label: '体育竞速', value: 19 },
  { label: '网络游戏', value: 20 },
  { label: '益智游戏', value: 21 },
  { label: '休闲娱乐', value: 22 },
];

export const PLATFORM_OPTIONS = [
  { label: 'Android', value: PlatformType.ANDROID, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'iOS', value: PlatformType.IPHONE, type: [AccessPlatformType.App, AccessPlatformType.SDK, AccessPlatformType.Extension] },
  { label: 'Mac', value: PlatformType.MAC, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'Windows', value: PlatformType.WINDOWS, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'Harmony', value: PlatformType.HARMONY, type: [AccessPlatformType.App, AccessPlatformType.SDK] },
  { label: 'VisionOS', value: PlatformType.VisionOS, type: [AccessPlatformType.App] },
  { label: 'Flutter', value: PlatformType.FLUTTER, type: [AccessPlatformType.Multi] },
  { label: 'Hippy', value: PlatformType.Hippy, type: [AccessPlatformType.Multi] },
  { label: 'Kuikly', value: PlatformType.Kuikly, type: [AccessPlatformType.Multi] },
  { label: '小程序', value: PlatformType.MiniProgram, type: [AccessPlatformType.Multi] },
];

export const PLATFORM_TYPE_OPTIONS = [
  { label: 'APP', value: AccessPlatformType.App },
  { label: 'SDK', value: AccessPlatformType.SDK },
  { label: 'Extension', value: AccessPlatformType.Extension },
  { label: '跨端应用', value: AccessPlatformType.Multi },
];

export const APPFORM_ALL_OPTIONS = [
  { label: 'APP', value: 1 },
  { label: 'Extension', value: 3 },
];

export const APPFORM_OPTIONS = [
  { label: 'APP', value: 1 },
];

export const ILLEGAL_REG = /^[\u4e00-\u9fa5a-zA-Z0-9=+/@#_-]+$/;
export const BUNDLE_ID_REG = /^[a-zA-Z0-9\\.]+$/;

export const DESC_REG = /^[\u4e00-\u9fa5a-zA-Z0-9.,#:*$\s@_\-\\()]+$/;

export const CreateApp = (props: { visible: boolean, handleCancel: () => void }) => {
  const { visible, handleCancel } = props;
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [formType, setFormType] = useState(PLATFORM_TYPE_OPTIONS[0].value);
  const [selectPlatform, setSelectPlatform] = useState<PlatformType>(PlatformType.ANDROID);
  const [form] = Form.useForm();

  const load = async () => {
    const departmentList = await GetDepartmentsByUser();
    if (Array.isArray(departmentList) && (departmentList?.[0]?.fullName ?? '') !== '') {
      const department = departmentList?.[0]?.fullName || '';
      if (department !== '') {
        form.setFieldsValue({ department });
      }
    }
  };

  useEffect(() => {
    if (!isInternal() && visible) {
      load();
    }
  }, [visible]);

  const handleSubmit = async (values: FormFieldType) => {
    if (!selectPlatform) {
      message.error('请选择接入的产品类型');
      return;
    }
    const params: IAddProductOaReq = {
      bundleId: values.bundleId,
      platformId: selectPlatform,
      name: values.productName,
      type: values.appType?.[1],
      ownerId: getUserId(),
      desc: '',
      appForm: values.appForm,
      admins: values.admins,
      department: values.department,
    };
    const data = await productOAServer.addProductOa(params);
    if (data.baseRsp?.code === BusiCode.BundleIDRepeat) {
      message.error(`${data?.baseRsp?.msg}`, 8);
      setConfirmLoading(false);
      return;
    }
    if (data.baseRsp?.code === BusiCode.Ok) {
      message.success('创建产品成功');
      handleCancel();
      location.replace(`${BASE_NAME}/overview?productId=${data?.appId}&pid=${params.platformId}`);
      setConfirmLoading(false);
    }
    setConfirmLoading(false);
    return;
  };

  const handleOk = () => {
    setConfirmLoading(true);
    form.validateFields()
      .then((values: FormFieldType) => {
        // 提交
        handleSubmit(values);
      })
      .catch(() => {
        setConfirmLoading(false);
      });
  };

  const AppType = ({ onChange, value = [] }: SelectAppType) => {
    const options = [
      {
        ...ALLTYPE_OPTIONS[0],
        children: ALLTYPE_OPTIONS.slice(2, 14),
      },
      {
        ...ALLTYPE_OPTIONS[1],
        children: ALLTYPE_OPTIONS.slice(14, 22),
      },
    ];
    const handleAppType = (AppTypeValue: (number | string)[], selectedOptions: Record<string, any>[]) => {
      // 当未选择时点击第一列，默认选中第二列的第一条
      if (AppTypeValue?.length === 1 && value?.[0] !== AppTypeValue[0]) {
        AppTypeValue.push(selectedOptions[0]?.children?.[0]?.value);
        onChange?.(AppTypeValue);
      } else if (AppTypeValue?.length === 1 && AppTypeValue?.[0] === value?.[0] && value?.length === 2) {
        AppTypeValue.push(value[1]);
        onChange?.(AppTypeValue);
      }
      if (AppTypeValue?.length === 2) {
        onChange?.(AppTypeValue);
      }
    };

    return <Cascader changeOnSelect onChange={handleAppType} value={value} options={options} placeholder="请选择" />;
  };

  const onPlatformChange = (platform: PlatformType) => {
    // platform === 2 ? setAppFormOption(APPFORM_ALL_OPTIONS) : setAppFormOption(APPFORM_OPTIONS);
    setSelectPlatform(platform);
  };

  const onAppFormChange = (e: any) => {
    const appForm = e.target.value;
    setFormType(appForm);
    const invalidPlatformList = PLATFORM_OPTIONS.filter(item => item.type.includes(appForm));
    setSelectPlatform(invalidPlatformList?.[0]?.value);
  };

  return (
    <Modal
      title="创建产品"
      width={700}
      visible={visible}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      onCancel={() => handleCancel()}
    >
      <Form form={form} layout={'vertical'}>
        <Form.Item
          label="接入平台"
          name="appForm"
          initialValue={PLATFORM_TYPE_OPTIONS[0].value}
          rules={[{ required: true, message: '请选择你的接入平台!' }]}
        >
          <Radio.Group onChange={onAppFormChange}>
            {PLATFORM_TYPE_OPTIONS.map(opt => (
                <Radio value={opt.value}>
                  {opt.label}
                </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Divider className='my-[16px] mt-0' />
        <div className={classNames(styles.platformContainer, 'mb-[24px]')}>
            {PLATFORM_OPTIONS.map((opt) => {
              const disabled = !opt.type.includes(formType);
              const checked = selectPlatform === opt.value;
              return (
                <div
                  className={classNames(styles.platforms, disabled ? styles.disabled : '', checked ? styles.active : '')}
                  onClick={() => {
                    if (disabled) return;
                    onPlatformChange(opt.value);
                  }}
                >
                  <span>{getAppIcon(`${opt.value}`, opt.value === PlatformType.FLUTTER)}</span>
                  <span>{opt.label}</span>
                </div>
              );
            })}
        </div>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="产品名称"
              name="productName"
              rules={[
                { required: true, message: '请输入产品名称!' },
                { max: 20, message: '字数不能超过20个字符!', type: 'string'  },
                { pattern: ILLEGAL_REG, message: '产品名称含有非法字符!' },
              ]}
            >
              <Input placeholder={'请输入仅包含中文字符、英文字母、数字以及特殊字符（=、+、/、@、#、_、-）的字符串'} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label={getTips('Bundle ID', 'BundleID，全称 Bundle Identifier ，也叫应用包名，是应用市场用来区分一款应用的唯一标识。Bugly平台使用用户创建产品时填写的BundleID来作为应用标识，与其他平台进行联动。Bugly平台内部，使用平台分配的AppID来作为Bugly产品的唯一标识。强烈建议用户填写正确的BundleID，如果创建产品后需要修改，请联系Bugly小助手。')}
              name="bundleId"
              rules={[
                { required: true, message: '请输入你的Bundle ID!' },
                { pattern: BUNDLE_ID_REG, message: 'Bundle ID含有非法字符!' },
              ]}
            >
              <Input placeholder={'请输入'} />
            </Form.Item>
          </Col>
        </Row>
        {!isInternal() && <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="部门组织架构"
              name="department"
              rules={[{ required: true, message: '' }]}
            >
              <Input
                disabled
                style={{ marginBottom: '5px', color: 'rgba(0, 0, 0, 0.85)', background: '#fff' }}
              />
            </Form.Item>
          </Col>
        </Row>}
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="产品类型"
              name="appType"
              rules={[{ required: true, message: '' }]}
            >
              <AppType />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              label="管理员"
              name="admins"
              initialValue={[getUserId()]}
              rules={[{ required: true, message: '请输入管理员名字!' }]}
            >
              <UserSelector placeholder={'请输入'} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
